import React from 'react';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaLock, FaUserShield, FaCookie, FaEnvelope } from 'react-icons/fa';

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: <FaShieldAlt />,
      title: "Information We Collect",
      content: [
        "Personal identification information (Name, email address, phone number, etc.)",
        "Business information for B2B transactions",
        "Payment and billing information",
        "Website usage data and analytics",
        "Communication preferences and history"
      ]
    },
    {
      icon: <FaLock />,
      title: "How We Use Your Information",
      content: [
        "Process and fulfill your orders",
        "Communicate about products and services",
        "Improve our website and customer experience",
        "Send marketing communications (with your consent)",
        "Comply with legal obligations",
        "Prevent fraud and enhance security"
      ]
    },
    {
      icon: <FaUserShield />,
      title: "Data Protection & Security",
      content: [
        "We implement industry-standard security measures to protect your data",
        "SSL encryption for all data transmission",
        "Regular security audits and updates",
        "Restricted access to personal information",
        "Secure payment processing through trusted providers",
        "Data backup and disaster recovery procedures"
      ]
    },
    {
      icon: <FaCookie />,
      title: "Cookies & Tracking",
      content: [
        "We use cookies to enhance your browsing experience",
        "Analytics cookies to understand website usage",
        "Preference cookies to remember your settings",
        "Marketing cookies (with your consent)",
        "You can control cookie preferences in your browser settings"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-dark-900 dark:to-dark-800">
      {/* Hero Section */}
      <div className="bg-primary-700 dark:bg-primary-900 text-white py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <FaShieldAlt className="text-6xl mx-auto mb-6 text-primary-200" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Your privacy is important to us. This policy outlines how we collect, use, and protect your personal information.
            </p>
            <p className="text-sm text-primary-200 mt-4">
              Last Updated: October 20, 2025
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
            At <strong>Worga Naturals</strong>, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage with our services.
          </p>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            By using our website, you consent to the practices described in this policy. If you do not agree with our policies and practices, please do not use our services.
          </p>
        </motion.div>

        {/* Policy Sections */}
        <div className="space-y-12">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-dark-700"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="text-3xl text-primary-600 dark:text-primary-400">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {section.title}
                </h2>
              </div>
              <ul className="space-y-3">
                {section.content.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-primary-600 dark:text-primary-400 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Additional Sections */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 space-y-8"
        >
          {/* Third-Party Disclosure */}
          <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Third-Party Disclosure
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:
            </p>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-3">
                <span className="text-primary-600 dark:text-primary-400">•</span>
                <span>Trusted service providers who assist in operating our website and conducting business</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary-600 dark:text-primary-400">•</span>
                <span>When required by law or to protect our rights</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary-600 dark:text-primary-400">•</span>
                <span>With your explicit consent</span>
              </li>
            </ul>
          </div>

          {/* Your Rights */}
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-2xl p-8 border border-primary-100 dark:border-primary-800">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Your Rights
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              You have the right to:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                <span className="text-primary-600 dark:text-primary-400">✓</span>
                <span>Access your personal data</span>
              </div>
              <div className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                <span className="text-primary-600 dark:text-primary-400">✓</span>
                <span>Correct inaccurate data</span>
              </div>
              <div className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                <span className="text-primary-600 dark:text-primary-400">✓</span>
                <span>Request data deletion</span>
              </div>
              <div className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                <span className="text-primary-600 dark:text-primary-400">✓</span>
                <span>Opt-out of marketing</span>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-800 dark:to-primary-900 rounded-2xl p-8 text-white text-center">
            <FaEnvelope className="text-5xl mx-auto mb-4 text-primary-200" />
            <h2 className="text-2xl font-bold mb-4">Questions About Privacy?</h2>
            <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
              If you have any questions or concerns about our Privacy Policy, please don't hesitate to contact us.
            </p>
            <a
              href="mailto:info@worganaturals.com"
              className="inline-flex items-center gap-2 bg-white text-primary-700 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
            >
              <FaEnvelope />
              Contact Us
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
